import { extractMessage, extractStack } from "@du/startup/errorCapture/clientErrorPayload";
import * as Sentry from "@sentry/vue";
import { activityTracker, type ApiCallRecord, type UserActionRecord } from "@utility/activityTracker";
import { fetchData } from "@utility/webServices";

export const POST_BUG_REPORT_ENDPOINT = "BugReport/post-bug-report";
export const RETRY_JIRA_ENDPOINT = (id: number) => `BugReport/bug-report/${id}/retry-jira`;

export interface BugReportResponse {
  Successful: boolean;
  Message: string;
  BugReportId: number | null;
  JiraTicketKey: string | null;
  AlreadyCreated: boolean;
}

type BugSource = "manual" | "frontendRouting" | "promise";

interface ReportBugOptions {
  source: BugSource;
  error?: unknown;
  description?: string;
  includeScreenshot?: boolean;
  screenshotBase64?: string | null;
  email?: string;
  submitTicket?: boolean;
}

interface ReportBugPayload {
  Source: BugSource;
  Description: string | null;
  Email: string | null;
  Message: string | null;
  Stack: string | null;
  Url: string;
  UserAgent: string;
  Timestamp: string;
  ScreenshotBase64: string | null;
  SentryContext: any | null;
  SubmitTicket: boolean;
  ApiCalls: ApiCallRecord[];
  UserActions: UserActionRecord[];
  OpenReplaySessionUrl: string | null;
}

export function useBugReport() {
  /**
   * Public API
   */
  const reportBug = async (options: ReportBugOptions): Promise<BugReportResponse> => {
    const scope = Sentry.getCurrentScope();

    // Add tags for categorization
    if (options.source) {
      scope?.setTag("source", options.source);
      scope?.setTag("bug-report-type", "manual");
    }
    //this is asynced here because this lib is sorta big to include in the main s1 entrypoint.
    //a chunk-load failure here must not abort the bug report, so the session URL is best-effort.
    let openReplaySessionUrl: string | null = null;
    try {
      const { tracker } = await import("@openreplay/tracker");
      openReplaySessionUrl = tracker.getSessionURL?.() ?? null;
    } catch (e) {
      console.warn("[Bug Report] Could not load OpenReplay tracker for session URL:", e);
    }

    if (openReplaySessionUrl) {
      scope?.setContext("OpenReplay", { sessionURL: openReplaySessionUrl });
    }

    // Add user-provided context to Sentry
    if (options.description) {
      scope?.setContext("Bug Report", {
        description: options.description,
        email: options.email,
        reportedAt: new Date().toISOString(),
      });
    }

    // Add activity data to Sentry context
    const activitySnapshot = activityTracker.getActivitySnapshot();
    scope?.setContext("Recent API Calls", {
      count: activitySnapshot.apiCalls.length,
      calls: activitySnapshot.apiCalls.slice(-5), // Last 5 calls
    });
    scope?.setContext("Recent User Actions", {
      count: activitySnapshot.userActions.length,
      actions: activitySnapshot.userActions.slice(-5), // Last 5 actions
    });

    // Capture as Sentry event (but won't send due to beforeSend config)
    if (options.error) {
      Sentry.captureException(options.error, {
        level: "error",
        tags: {
          source: options.source,
          "user-reported": "true",
        },
      });
    } else {
      Sentry.captureMessage(`User reported bug: ${options.description?.slice(0, 100) || "No description"}`, {
        level: "info",
        tags: {
          source: options.source,
          "user-reported": "true",
        },
      });
    }

    const screenshot =
      options.screenshotBase64 !== undefined
        ? options.screenshotBase64
        : options.includeScreenshot === false
          ? null
          : await captureScreenshot();

    const payload: ReportBugPayload = {
      Source: options.source,
      Description: options.description ?? null,
      Email: options.email ?? null,
      Message: extractMessage(options.error),
      Stack: extractStack(options.error),
      Url: window.location.href,
      UserAgent: navigator.userAgent,
      Timestamp: new Date().toISOString(),
      ScreenshotBase64: screenshot,
      SentryContext: scope ? serializeScope(scope) : null,
      SubmitTicket: options.submitTicket ?? false,
      ApiCalls: activitySnapshot.apiCalls,
      UserActions: activitySnapshot.userActions,
      OpenReplaySessionUrl: openReplaySessionUrl,
    };

    return await postBugReport(payload);
  };

  return {
    reportBug,
  };
}

const postBugReport = async (payload: ReportBugPayload): Promise<BugReportResponse> => {
  const response = await fetchData<BugReportResponse>(POST_BUG_REPORT_ENDPOINT, payload);
  return (
    response ?? {
      Successful: false,
      Message: "No response from server.",
      BugReportId: null,
      JiraTicketKey: null,
      AlreadyCreated: false,
    }
  );
};

export const retryJiraTicket = async (bugReportId: number): Promise<BugReportResponse> => {
  const response = await fetchData<BugReportResponse>(RETRY_JIRA_ENDPOINT(bugReportId), {});
  return (
    response ?? {
      Successful: false,
      Message: "No response from server.",
      BugReportId: bugReportId,
      JiraTicketKey: null,
      AlreadyCreated: false,
    }
  );
};

/**
 * Captures a screenshot of the current page, excluding modals.
 * @returns Base64 string of the screenshot or null if failed.
 */
export const captureScreenshot = async (): Promise<string | null> => {
  try {
    //this is asynced here because this lib is sorta big to include in the main s1 entrypoint
    const { default: html2canvas } = await import("html2canvas");
    const canvas = await html2canvas(document.body, {
      ignoreElements: (el) =>
        el.classList.contains("modal") ||
        el.classList.contains("modal-backdrop") ||
        el.id === "bug-report-container",
      useCORS: true,
    });

    return canvas.toDataURL("image/png");
  } catch (e) {
    console.warn("[Bug Report] Could not capture screenshot:", e);
    return null;
  }
};

/**
 * Serializes the Sentry scope for inclusion in bug reports.
 * Captures all the rich context Sentry has been tracking.
 * @param scope
 * @returns
 */
export const serializeScope = (scope: any) => {
  return {
    tags: scope._tags ?? {},
    extras: scope._extra ?? {},
    contexts: scope._contexts ?? {},
    user: scope._user ?? null,
    breadcrumbs: scope._breadcrumbs ?? [],
    level: scope._level ?? "info",
    fingerprint: scope._fingerprint ?? [],
  };
};
